import {
    ALL_TENANT_ROLES,
    OWNER_AND_ADMIN_ROLES,
    OWNER_ONLY_ROLES,
    USER_ROLES,
    UserRole,
} from '@core/auth/constants/user-roles.const';

const LEGACY_OWNER_ALIASES = new Set(['superuser']);

export function isSuperuserRole(role: string | null | undefined): boolean {
    return role?.trim().toLowerCase() === 'superuser';
}

const ASSIGNABLE_TENANT_ROLES: readonly UserRole[] = [
    USER_ROLES.OWNER,
    USER_ROLES.ADMIN,
    USER_ROLES.MEMBER,
];

export function canManageUsers(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

const SUPERUSER_ROLE = 'superuser';

export type UserRoleAssignmentMode = 'create' | 'edit';

function exactRole(role: string | null | undefined): string {
    return role?.trim().toLowerCase() ?? '';
}

export function isOwnerRole(role: string | null | undefined): boolean {
    return exactRole(role) === USER_ROLES.OWNER;
}

export function isAdminRole(role: string | null | undefined): boolean {
    return exactRole(role) === USER_ROLES.ADMIN;
}

export function isMemberRole(role: string | null | undefined): boolean {
    return exactRole(role) === USER_ROLES.MEMBER;
}

export function canChangeManagedUserRole(
    actorRole: string | null | undefined,
    currentTargetRole: string | null | undefined,
): boolean {
    if (!currentTargetRole?.trim()) {
        return false;
    }

    if (isSuperuserRole(currentTargetRole)) {
        return false;
    }

    if (isOwnerRole(actorRole) && isOwnerRole(currentTargetRole)) {
        return false;
    }

    if (isAdminRole(actorRole) && (isAdminRole(currentTargetRole) || isOwnerRole(currentTargetRole))) {
        return false;
    }

    return isSuperuserRole(actorRole) || isOwnerRole(actorRole) || isAdminRole(actorRole);
}

export function canEditManagedUser(
    actorRole: string | null | undefined,
    targetRole: string | null | undefined,
): boolean {
    if (isSuperuserRole(actorRole)) {
        return true;
    }

    if (isOwnerRole(actorRole)) {
        return !isSuperuserRole(targetRole);
    }

    if (isAdminRole(actorRole)) {
        return isAdminRole(targetRole) || isMemberRole(targetRole);
    }

    return false;
}

export function canChangeManagedUserStatus(
    actorRole: string | null | undefined,
    targetRole: string | null | undefined,
): boolean {
    if (isSuperuserRole(actorRole)) {
        return true;
    }

    if (isOwnerRole(actorRole)) {
        return !isSuperuserRole(targetRole);
    }

    if (isAdminRole(actorRole)) {
        return isAdminRole(targetRole) || isMemberRole(targetRole);
    }

    return false;
}

export function assignableUserRoles(
    actorRole: string | null | undefined,
    mode: UserRoleAssignmentMode = 'create',
    currentTargetRole?: string | null,
): string[] {
    if (mode === 'edit' && !canChangeManagedUserRole(actorRole, currentTargetRole)) {
        const current = exactRole(currentTargetRole);
        return current ? [current] : [];
    }

    if (isSuperuserRole(actorRole)) {
        const roles: string[] = [...ASSIGNABLE_TENANT_ROLES];
        if (mode === 'create') {
            roles.push(SUPERUSER_ROLE);
        }
        return roles;
    }

    if (isOwnerRole(actorRole)) {
        return [...ASSIGNABLE_TENANT_ROLES];
    }

    if (isAdminRole(actorRole)) {
        return [USER_ROLES.ADMIN, USER_ROLES.MEMBER];
    }

    return [];
}

export function canAssignUserRole(
    actorRole: string | null | undefined,
    targetRole: string,
    mode: UserRoleAssignmentMode = 'create',
    currentTargetRole?: string | null,
): boolean {
    const normalizedTarget = exactRole(targetRole);
    if (!normalizedTarget) {
        return false;
    }

    return assignableUserRoles(actorRole, mode, currentTargetRole).includes(normalizedTarget);
}

export function canManageTenants(role: string | null | undefined): boolean {
    return isSuperuserRole(role);
}

export function canCreateTenant(role: string | null | undefined): boolean {
    return isSuperuserRole(role);
}

export function canManageWhatsappAccounts(role: string | null | undefined): boolean {
    return isSuperuserRole(role);
}

export function normalizeUserRole(role: string | null | undefined): UserRole | null {
    if (!role) {
        return null;
    }

    const normalized = role.trim().toLowerCase();

    if (LEGACY_OWNER_ALIASES.has(normalized)) {
        return USER_ROLES.OWNER;
    }

    if (
        normalized === USER_ROLES.OWNER
        || normalized === USER_ROLES.ADMIN
        || normalized === USER_ROLES.MEMBER
    ) {
        return normalized;
    }

    return null;
}

export function hasAnyRole(role: string | null | undefined, allowedRoles: readonly UserRole[]): boolean {
    const normalizedRole = normalizeUserRole(role);
    return normalizedRole !== null && allowedRoles.includes(normalizedRole);
}

export function canAccessSettings(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_ONLY_ROLES);
}

export function canAccessAdminTab(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canAccessRules(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canManageRules(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canManageAgentTools(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canConnectWhatsApp(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_ONLY_ROLES);
}

export function canManageCampaigns(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canAccessKnowledgeBase(role: string | null | undefined): boolean {
    return hasAnyRole(role, ALL_TENANT_ROLES);
}

export function canManageKnowledgeBase(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canAccessAssistant(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}

export function canReconcileConversationControl(role: string | null | undefined): boolean {
    return hasAnyRole(role, OWNER_AND_ADMIN_ROLES);
}
